/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Local Storage Module - Управление кэшированием данных
 */

// Импорт функций из utils (renamed)
import { unzip, lastModified } from '../utils/local-storage-utils.js';

(function(window) {
    'use strict';

    const LocalStorage = {};

    /**
     * Проверяет и получает дату из localStorage
     */
    LocalStorage.getFilesDate = function() {
        const filesDate = 'filesDate' in localStorage ? Number(localStorage['filesDate']) : null;
        // Если filesDate некорректное, возвращаем null
        return (filesDate && !isNaN(filesDate)) ? filesDate : null;
    };

    /**
     * Сохраняет дату файлов в localStorage
     */
    LocalStorage.setFilesDate = function(date) {
        localStorage['filesDate'] = date;
    };

    /**
     * Получает список файлов из localStorage
     */
    LocalStorage.getFilesList = function() {
        if ('filesList' in localStorage) {
            try {
                const compressed = localStorage['filesList'];
                return window.unzip ? window.unzip(compressed) : JSON.parse(compressed);
            } catch (e) {
                if (debugMode === '1') {
                console.error('Error decompressing files list:', e);
            }
                return null;
            }
        }
        return null;
    };

    /**
     * Сохраняет список файлов в localStorage
     */
    LocalStorage.setFilesList = function(filesList) {
        try {
            // Сохраняем в сжатом виде
            localStorage.setItem('filesList', filesList);
            return true;
        } catch (e) {
            if (debugMode === '1') {
                console.error('Error saving files list:', e);
            }
            // При ошибке очищаем localStorage
            LocalStorage.clearAll();
            return false;
        }
    };

    /**
     * Очищает весь localStorage
     */
    LocalStorage.clearAll = function() {
        for (const key in window.localStorage) {
            window.localStorage.removeItem(key);
        }
    };

    /**
     * Проверяет, нужно ли обновить список файлов
     */
    LocalStorage.shouldUpdateFilesList = function(serverDate, localDate) {
        if (localDate === null) return true;
        if (serverDate === null || isNaN(serverDate)) return false;
        return serverDate > localDate;
    };

    /**
     * Фильтрует конфиденциальные файлы
     */
    LocalStorage.filterConfidentialFiles = function(filesList) {
        // Регулярные выражения для фильтрации
        const audioOrTapesRegex = /(AUDIO|Tapes)[\\/]/;
        const confidentialRegex = /confid(ential)?|class *viii/i;
        const complexConfidentialRegex = /\(confid(ential)?\)|confidential (guardian|intelligence)|(disconnections)( *[\u2013\u2014-] *)?confidential|confidencial/i;
        
        const originalCount = filesList.length;
        const filtered = filesList.filter(({ n, p }) => {
            const filePath = `${p}/${n}`;
            return !(
                (audioOrTapesRegex.test(filePath) && confidentialRegex.test(filePath)) ||
                complexConfidentialRegex.test(filePath)
            );
        });
        
        if (window.HyperLog) {
            HyperLog.debug("Confidential files filtered", { 
                originalCount: originalCount,
                filteredCount: filtered.length,
                removedCount: originalCount - filtered.length
            });
        }
        
        return filtered;
    };

    /**
     * Загружает и кэширует список файлов
     */
    LocalStorage.loadFilesList = function(options = {}) {
        const {
            url = "/api/files/",
            forceUpdate = false,
            onSuccess = null,
            onError = null
        } = options;

        let filesListUpdate = true;
        let filesDateHead = null;
        let filesDate = LocalStorage.getFilesDate();
        
        if (window.HyperLog) {
            HyperLog.debug("Loading files list", { 
                url: url,
                filesDateInStorage: filesDate,
                forceUpdate: forceUpdate
            });
        }

        // Проверяем кэш
        if (!forceUpdate && filesDate !== null) {
            const cachedList = LocalStorage.getFilesList();
            if (cachedList) {
                // Проверяем дату последнего изменения на сервере
                filesDateHead = window.lastModified ? window.LocalStorage.lastModified(url) : null;
                
                if (window.HyperLog) {
                    HyperLog.debug("Checking cache validity", { 
                        filesDateHead: filesDateHead, 
                        filesDate: filesDate,
                        cacheValid: !LocalStorage.shouldUpdateFilesList(filesDateHead, filesDate)
                    });
                }
                
                if (!LocalStorage.shouldUpdateFilesList(filesDateHead, filesDate)) {
                    // Используем кэшированные данные
                    const filtered = LocalStorage.filterConfidentialFiles(cachedList);
                    if (onSuccess) onSuccess(filtered);
                    return filtered;
                }
            }
        }

        // Загружаем с сервера
        $.ajax({ 
            url: url, 
            type: "GET", 
            async: true 
        }).done((data, status, xhr) => {
            if (window.HyperLog) { 
                HyperLog.debug("Files list response received", { bytes: data.length }); 
            }
            
            // Сохраняем сжатые данные
            if (LocalStorage.setFilesList(data.toString())) {
                // Распаковываем для использования
                const filesList = window.unzip ? window.unzip(data.toString()) : JSON.parse(data.toString());
                
                if (window.HyperLog) {
                    HyperLog.debug("Files list decompressed", { 
                        items: filesList.length, 
                        sample: filesList.slice(0, 5) 
                    });
                }
                
                // Обновляем дату
                filesDate = filesDateHead !== null && !isNaN(filesDateHead) ? filesDateHead : Date.now();
                LocalStorage.setFilesDate(filesDate);
                
                // Фильтруем и возвращаем
                const filtered = LocalStorage.filterConfidentialFiles(filesList);
                if (onSuccess) onSuccess(filtered);
                return filtered;
            }
        }).fail((xhr, status, error) => {
            if (window.HyperLog) { 
                HyperLog.debug("Files list request failed", { 
                    status: xhr.status, 
                    error: error 
                }); 
            }
            if (debugMode === '1') {
                console.error('Error getting files list:', error);
            }
            if (onError) onError(error);
        });
    };

    /**
     * Сохраняет произвольные данные в localStorage
     */
    LocalStorage.setItem = function(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (e) {
            if (debugMode === '1') {
                console.error('Error saving to localStorage:', e);
            }
            return false;
        }
    };

    /**
     * Получает данные из localStorage
     */
    LocalStorage.getItem = function(key, defaultValue = null) {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (e) {
            if (debugMode === '1') {
                console.error('Error reading from localStorage:', e);
            }
            return defaultValue;
        }
    };

    /**
     * Удаляет элемент из localStorage
     */
    LocalStorage.removeItem = function(key) {
        localStorage.removeItem(key);
    };

    /**
     * Алиас для getItem (для совместимости с современными API)
     */
    LocalStorage.get = function(key, defaultValue = null) {
        return LocalStorage.getItem(key, defaultValue);
    };

    /**
     * Алиас для setItem (для совместимости с современными API)
     */
    LocalStorage.set = function(key, value) {
        return LocalStorage.setItem(key, value);
    };

    /**
     * Сохраняет фильтр в localStorage
     */
    LocalStorage.saveFilter = function(filterName, value) {
        const filters = LocalStorage.getItem('savedFilters', {});
        filters[filterName] = value;
        return LocalStorage.setItem('savedFilters', filters);
    };

    /**
     * Загружает все сохранённые фильтры
     */
    LocalStorage.loadFilters = function() {
        return LocalStorage.getItem('savedFilters', {});
    };

    /**
     * Очищает все сохранённые фильтры
     */
    LocalStorage.clearFilters = function() {
        return LocalStorage.removeItem('savedFilters');
    };

    // Assign imported utility functions to LocalStorage for backward compatibility
    LocalStorage.unzip = unzip;
    LocalStorage.lastModified = lastModified;

    // Экспорт модуля
    window.LocalStorage = LocalStorage;

    // ES Module export for modern imports
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = LocalStorage;
    } else if (typeof window !== 'undefined') {
        window.localStorageManager = LocalStorage;
    }

})(window);

// ES Module export
export const localStorageManager = window.LocalStorage || {};
export { unzip, lastModified }; 